import { z } from "zod";
import {
  ACTIVITY_COMPETITION_MODES,
  ACTIVITY_SESSION_FORMATS,
  ACTIVITY_SESSION_JOIN_REQUEST_STATUS,
  ACTIVITY_SESSION_PRICING_TYPE,
  ACTIVITY_SESSION_VISIBILITY,
} from "@/db/schema/activity-session";
import {
  ACTIVITY_SESSION_SPLIT_MODE,
  ACTIVITY_SESSION_SPLIT_PRICING,
} from "@/db/schema/activity-session-split";

export const activitySessionSchema = z
  .object({
    activityId: z.string().min(1, "Please select an activity"),
    title: z.string().trim().min(1, "Please enter a title"),
    description: z
      .string()
      .trim()
      .max(2000, "Keep the session post under 2000 characters")
      .default(""),
    visibility: z.enum(ACTIVITY_SESSION_VISIBILITY).default("public"),
    startDate: z.string().min(1, "Please select a date"),
    startTime: z.string().min(1, "Please select a start time"),
    endTime: z.string().min(1, "Please select an end time"),
    locationName: z.string().trim().min(1, "Please select a location"),
    address: z.string().trim().min(1, "Please select a location"),
    latitude: z.coerce
      .number()
      .min(-90, "Invalid latitude")
      .max(90, "Invalid latitude"),
    longitude: z.coerce
      .number()
      .min(-180, "Invalid longitude")
      .max(180, "Invalid longitude"),
    minParticipants: z.coerce.number().int().min(1),
    maxParticipants: z.coerce.number().int().min(1),
    competitionMode: z.enum(ACTIVITY_COMPETITION_MODES).default("casual"),
    sessionFormat: z.enum(ACTIVITY_SESSION_FORMATS).nullable().optional(),
    minSkillLevel: z.coerce.number().int().min(1).max(7).nullable().optional(),
    maxSkillLevel: z.coerce.number().int().min(1).max(7).nullable().optional(),
    requiresApproval: z.boolean().default(false),
    pricingType: z.enum(ACTIVITY_SESSION_PRICING_TYPE).default("free"),
    pricePerPersonAmountMinor: z.coerce.number().int().min(0).default(0),
    currency: z.string().trim().min(1).default("INR"),
    venueBookingId: z.number().int().positive().nullable().default(null),
    collectMoney: z.boolean().default(false),
    splitPricing: z
      .enum(ACTIVITY_SESSION_SPLIT_PRICING)
      .default("divide_total"),
    splitMode: z.enum(ACTIVITY_SESSION_SPLIT_MODE).default("offline"),
    splitAmountMinor: z.coerce.number().int().min(0).default(0),
    organizerPlaying: z.boolean().default(true),
    extrasAllowed: z.boolean().default(false),
    extrasAllowancePercent: z.coerce.number().int().min(0).max(100).default(50),
    extrasNote: z.string().trim().max(200).default(""),
  })
  .refine((data) => data.maxParticipants >= data.minParticipants, {
    message: "Max participants must be at least min participants",
    path: ["maxParticipants"],
  })
  .refine((data) => !data.collectMoney || data.splitAmountMinor > 0, {
    message: "Enter the amount to collect",
    path: ["splitAmountMinor"],
  })
  .refine(
    (data) => {
      if (data.pricingType === "free") {
        return data.pricePerPersonAmountMinor === 0;
      }
      return data.pricePerPersonAmountMinor > 0;
    },
    {
      message: "Please enter a valid price per person",
      path: ["pricePerPersonAmountMinor"],
    },
  )
  .refine(
    (data) => {
      const start = new Date(`${data.startDate}T${data.startTime}`);
      const endSameDay = new Date(`${data.startDate}T${data.endTime}`);
      const end =
        endSameDay.getTime() > start.getTime()
          ? endSameDay
          : new Date(endSameDay.getTime() + 24 * 60 * 60 * 1000);

      const durationMs = end.getTime() - start.getTime();
      const minDurationMs = 15 * 60 * 1000;
      const maxDurationMs = 12 * 60 * 60 * 1000;

      return durationMs >= minDurationMs && durationMs <= maxDurationMs;
    },
    {
      message: "End time must be 15m–12h after start time",
      path: ["endTime"],
    },
  )
  .refine(
    (data) => {
      const start = new Date(`${data.startDate}T${data.startTime}`);
      return start > new Date();
    },
    {
      message: "Start time must be in the future",
      path: ["startTime"],
    },
  );

export type ActivitySessionValues = z.infer<typeof activitySessionSchema>;

export const activitySessionJoinRequestSchema = z
  .object({
    activitySessionId: z.number().int().positive(),
    preferredTeam: z.enum(["team1", "team2"] as const).optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();

export const cancelMyActivitySessionJoinRequestSchema = z
  .object({
    requestId: z.number().int().positive(),
  })
  .strict();

export const listMyActivitySessionJoinRequestsSchema = z
  .object({
    status: z.enum(ACTIVITY_SESSION_JOIN_REQUEST_STATUS).optional(),
    cursor: z.number().int().positive().optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();

export const getMyActivitySessionJoinRequestSchema = z
  .object({
    requestId: z.number().int().positive(),
  })
  .strict();

export const getMyPendingActivitySessionJoinRequestSchema = z
  .object({
    activitySessionId: z.number().int().positive(),
  })
  .strict();

export const listActivitySessionJoinRequestsForCreatorSchema = z
  .object({
    status: z.enum(ACTIVITY_SESSION_JOIN_REQUEST_STATUS).optional(),
    activitySessionId: z.number().int().positive().optional(),
    cursor: z.number().int().positive().optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();

export const reviewActivitySessionJoinRequestSchema = z
  .object({
    requestId: z.number().int().positive(),
    approve: z.boolean(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();

export type ActivitySessionJoinRequestValues = z.infer<
  typeof activitySessionJoinRequestSchema
>;
export type CancelMyActivitySessionJoinRequestValues = z.infer<
  typeof cancelMyActivitySessionJoinRequestSchema
>;
export type ListMyActivitySessionJoinRequestsValues = z.infer<
  typeof listMyActivitySessionJoinRequestsSchema
>;
export type GetMyActivitySessionJoinRequestValues = z.infer<
  typeof getMyActivitySessionJoinRequestSchema
>;
export type GetMyPendingActivitySessionJoinRequestValues = z.infer<
  typeof getMyPendingActivitySessionJoinRequestSchema
>;
export type ListActivitySessionJoinRequestsForCreatorValues = z.infer<
  typeof listActivitySessionJoinRequestsForCreatorSchema
>;
export type ReviewActivitySessionJoinRequestValues = z.infer<
  typeof reviewActivitySessionJoinRequestSchema
>;
